import { formatDistanceToNow } from "date-fns";
import {
	Activity,
	AlertCircle,
	CheckCircle,
	Clock,
	Lightbulb,
	Plus,
	RefreshCw,
	Target,
	TrendingDown,
	TrendingUp,
} from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Textarea,
} from "@/components/primitives";
import { useAuth } from "@/contexts/AuthContext";
import { aiService } from "@/lib/ai-service";
import { supabase } from "@/lib/supabase";

interface ReflectionAnalysisData {
	positiveObservations: Array<{
		observation: string;
		frequency: number;
		impact: string;
		signals: string[];
		context: string;
	}>;
	negativeObservations: Array<{
		observation: string;
		frequency: number;
		impact: string;
		signals: string[];
		context: string;
	}>;
	impactTrends: {
		overall: "improving" | "declining" | "stable";
		recentTrend: string;
		significantChanges: string[];
	};
	signalPatterns: Array<{
		signal: string;
		frequency: number;
		correlation: string;
		impact: string;
	}>;
	insights: {
		keyFindings: string[];
		progressIndicators: string[];
		concernAreas: string[];
	};
	recommendations: Array<{
		title: string;
		description: string;
		priority?: "high" | "medium" | "low";
		timeframe?: string;
	}>;
}

interface AnalysisRecord {
	id: string;
	user_id: string;
	reflection_id: string;
	analysis_date: string;
	checkin_count: number;
	last_checkin_date: string | null;
	analysis_data: ReflectionAnalysisData;
	created_at: string;
	updated_at: string;
}

export interface ReflectionAnalysisRef {
	refresh: () => void;
	generateAnalysis: () => void;
	getAnalysis: () => ReflectionAnalysisData | null;
}

interface ReflectionAnalysisProps {
	reflectionId: string;
	reflectionTitle: string;
	reflectionDescription: string;
	className?: string;
}

export const ReflectionAnalysis = forwardRef<
	ReflectionAnalysisRef,
	ReflectionAnalysisProps
>(
	(
		{ reflectionId, reflectionTitle, reflectionDescription, className },
		ref,
	) => {
		const { user } = useAuth();
		const [analysis, setAnalysis] = useState<ReflectionAnalysisData | null>(
			null,
		);
		const [loading, setLoading] = useState(false);
		const [generating, setGenerating] = useState(false);
		const [error, setError] = useState<string>("");
		const [lastUpdated, setLastUpdated] = useState<string>("");
		const [checkInCount, setCheckInCount] = useState(0);
		const [totalCheckInCount, setTotalCheckInCount] = useState(0);
		const [loadingCheckInCount, setLoadingCheckInCount] = useState(false);
		const [convertingRecommendation, setConvertingRecommendation] = useState<
			number | null
		>(null);
		const [editingRecIndex, setEditingRecIndex] = useState<number | null>(null);
		const [draftTitle, setDraftTitle] = useState<string>("");
		const [draftDescription, setDraftDescription] = useState<string>("");

		useImperativeHandle(ref, () => ({
			refresh: fetchAnalysis,
			generateAnalysis,
			getAnalysis: () => analysis,
		}));

		useEffect(() => {
			if (reflectionId && user) {
				fetchAnalysis();
				fetchCheckInCount();
			}
		}, [reflectionId, user]);

		const fetchCheckInCount = async () => {
			if (!user) return;

			setLoadingCheckInCount(true);
			try {
				// Count check-ins from the last 28 days only
				const twentyEightDaysAgo = new Date();
				twentyEightDaysAgo.setDate(twentyEightDaysAgo.getDate() - 28);

				const { count, error } = await supabase
					.from("daily_checkins")
					.select("*", { count: "exact", head: true })
					.eq("user_id", user.id)
					.gte("created_at", twentyEightDaysAgo.toISOString());

				if (error) {
					console.error("Error fetching check-in count:", error);
					return;
				}

				setTotalCheckInCount(count ?? 0);
			} catch (error) {
				console.error("Error in fetchCheckInCount:", error);
			} finally {
				setLoadingCheckInCount(false);
			}
		};

		const fetchAnalysis = async () => {
			if (!user || !reflectionId) return;

			setLoading(true);
			setError("");

			try {
				// Get the most recent analysis for this reflection
				const { data, error } = await supabase
					.from("reflection_analysis")
					.select("*")
					.eq("user_id", user.id)
					.eq("reflection_id", reflectionId)
					.order("analysis_date", { ascending: false })
					.limit(1);

				if (error) {
					console.error("Error fetching reflection analysis:", error);
					setError("Failed to load analysis");
					return;
				}

				if (data && data.length > 0) {
					const analysisRecord = data[0] as AnalysisRecord;
					setAnalysis(analysisRecord.analysis_data);
					setLastUpdated(analysisRecord.updated_at);
					setCheckInCount(analysisRecord.checkin_count);
				} else {
					setAnalysis(null);
				}
			} catch (error) {
				console.error("Error in fetchAnalysis:", error);
				setError("Failed to load analysis");
			} finally {
				setLoading(false);
			}
		};

		const generateAnalysis = async () => {
			if (!user || !reflectionId) return;

			setGenerating(true);
			setError("");

			try {
				// Calculate date 28 days ago for eligibility check
				const twentyEightDaysAgo = new Date();
				twentyEightDaysAgo.setDate(twentyEightDaysAgo.getDate() - 28);

				// First, check check-in count from the last 28 days (eligibility requirement)
				const { count: recentCount, error: countError } = await supabase
					.from("daily_checkins")
					.select("*", { count: "exact", head: true })
					.eq("user_id", user.id)
					.gte("created_at", twentyEightDaysAgo.toISOString());

				if (countError) {
					throw new Error("Failed to fetch check-in count");
				}

				const currentCheckInCount = recentCount ?? 0;

				if (currentCheckInCount < 3) {
					setError(
						`You need at least 3 check-ins in the last 28 days to generate an analysis. You currently have ${currentCheckInCount} check-in${currentCheckInCount !== 1 ? "s" : ""} in the last 28 days.`,
					);
					setGenerating(false);
					return;
				}

				// Calculate date 3 months ago for fetching analysis data
				const threeMonthsAgo = new Date();
				threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

				// Fetch check-ins from the last 3 months for analysis (more data for better insights)
				const { data: checkIns, error: checkInsError } = await supabase
					.from("daily_checkins")
					.select(
						"id, created_at, general_feeling, day_description, reflection_feeling, reflection_impact, reflection_reasons, signals_noticed",
					)
					.eq("user_id", user.id)
					.gte("created_at", threeMonthsAgo.toISOString())
					.order("created_at", { ascending: true });

				if (checkInsError) {
					throw new Error("Failed to fetch check-ins for analysis");
				}

				if (!checkIns || checkIns.length < 3) {
					setError(
						`You need at least 3 check-ins in the last 28 days to generate an analysis. You currently have ${checkIns?.length ?? 0} check-in${checkIns?.length !== 1 ? "s" : ""} available.`,
					);
					setGenerating(false);
					return;
				}

				// Filter check-ins that have relevant data for reflection analysis
				// Look for check-ins with reflection-specific data OR general check-ins that mention keywords related to this reflection
				const reflectionKeywords = [
					...reflectionTitle.toLowerCase().split(" "),
					...reflectionDescription.toLowerCase().split(" "),
				].filter((word) => word.length > 3);

				const relevantCheckIns = checkIns.filter((checkIn) => {
					// Include check-ins with explicit reflection data
					const hasReflectionData =
						checkIn.reflection_feeling !== null ||
						checkIn.reflection_impact ||
						checkIn.reflection_reasons ||
						(checkIn.signals_noticed && checkIn.signals_noticed.length > 0);

					// Include check-ins that mention reflection keywords in day description
					const hasRelevantContent = reflectionKeywords.some((keyword) =>
						checkIn.day_description?.toLowerCase().includes(keyword),
					);

					return hasReflectionData || hasRelevantContent;
				});

				// If no specifically relevant check-ins, use general check-ins for broader analysis
				const checkInsForAnalysis =
					relevantCheckIns.length > 0 ? relevantCheckIns : checkIns.slice(-10); // Last 10 check-ins

				if (checkInsForAnalysis.length === 0) {
					setError(
						"No check-in data available for analysis. Please complete some check-ins first.",
					);
					return;
				}

				// Debug: Log data being sent for analysis
				console.log("🔍 Debug: Reflection analysis data being sent:", {
					reflection: {
						id: reflectionId,
						title: reflectionTitle,
						description: reflectionDescription,
					},
					totalCheckIns: checkIns.length,
					relevantCheckIns: relevantCheckIns.length,
					checkInsForAnalysis: checkInsForAnalysis.length,
					sampleCheckIn: checkInsForAnalysis[0],
					checkInsData: checkInsForAnalysis.map((ci) => ({
						id: ci.id,
						created_at: ci.created_at,
						general_feeling: ci.general_feeling,
						day_description: ci.day_description?.substring(0, 100) + "...",
						reflection_feeling: ci.reflection_feeling,
						reflection_impact: ci.reflection_impact,
						reflection_reasons: ci.reflection_reasons,
						signals_noticed: ci.signals_noticed,
					})),
				});

				// Call AI service to analyze reflection progress
				const analysisResult = await aiService.analyzeReflectionProgress(
					{
						id: reflectionId,
						title: reflectionTitle,
						description: reflectionDescription,
					},
					checkInsForAnalysis,
				);

				// Save analysis to database
				const today = new Date().toISOString().split("T")[0];
				const lastCheckInDate =
					checkInsForAnalysis[checkInsForAnalysis.length - 1]?.created_at;

				console.log("✅ Debug: analysisResult received", analysisResult);

				const { error: saveError } = await supabase
					.from("reflection_analysis")
					.upsert(
						{
							user_id: user.id,
							reflection_id: reflectionId,
							analysis_date: today,
							checkin_count: checkInsForAnalysis.length,
							last_checkin_date: lastCheckInDate,
							analysis_data: analysisResult,
						},
						{
							onConflict: "user_id,reflection_id,analysis_date",
						},
					);

				if (saveError) {
					console.error("Error saving analysis:", saveError);
					// Don't throw here, we still want to show the analysis
				}

				setAnalysis(analysisResult);
				setCheckInCount(checkInsForAnalysis.length);
				setLastUpdated(new Date().toISOString());

				// Ensure UI shows fresh data from DB as well
				try {
					await fetchAnalysis();
					await fetchCheckInCount();
				} catch (_) {}
			} catch (error) {
				console.error("Error generating reflection analysis:", error);
				setError(
					error instanceof Error
						? error.message
						: "Failed to generate analysis",
				);
			} finally {
				setGenerating(false);
			}
		};

		const getTrendIcon = (trend: string) => {
			switch (trend) {
				case "improving":
					return <TrendingUp className="h-4 w-4 text-green-600" />;
				case "declining":
					return <TrendingDown className="h-4 w-4 text-red-600" />;
				default:
					return <Activity className="h-4 w-4 text-blue-600" />;
			}
		};

		const getPriorityColor = (priority: string) => {
			switch (priority) {
				case "high":
					return "destructive";
				case "medium":
					return "secondary";
				default:
					return "outline";
			}
		};

		// Format helper to humanize underscore-separated tokens
		const formatSignal = (s: string) =>
			typeof s === "string" ? s.replace(/_/g, " ").trim() : (s as any);

		// Ensure content addresses you directly (no "the user"), and remove explicit dates
		const sanitizeText = (s: string) => {
			if (typeof s !== "string") return s as any;
			let t = s;
			// Handle possessives first to avoid producing "you's/you’s"
			t = t
				.replace(/\bthe user['’]s\b/gi, "your")
				.replace(/\buser['’]s\b/gi, "your")
				.replace(/\bthe mentee['’]s\b/gi, "your")
				.replace(/\bmentee['’]s\b/gi, "your")
				.replace(/\byou['’]s\b/gi, "your");
			// Then handle non-possessive references
			t = t.replace(/\bthe user\b/gi, "you").replace(/\bthe mentee\b/gi, "you");
			// Remove ISO-like dates (YYYY-MM-DD) and Month day, year formats
			t = t
				.replace(/\b20\d{2}-\d{2}-\d{2}\b/g, "")
				.replace(
					/\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2},\s*\d{4}\b/gi,
					"",
				)
				.replace(
					/\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?)\s+\d{1,2}\b/gi,
					"",
				)
				.replace(/\s{2,}/g, " ")
				.trim();
			return t;
		};

		// Convert normalized 1–10 to approximate mentions across analyzed check-ins
		const approxMentions = (freq: number) => {
			const f = Math.min(10, Math.max(0, Number(freq) || 0));
			return Math.max(1, Math.round((f / 10) * Math.max(1, checkInCount)));
		};

		const convertRecommendationToAction = async (
			recommendation: any,
			index: number,
		) => {
			if (!user || !reflectionId) return;

			setConvertingRecommendation(index);

			try {
				// Convert priority text to number
				const priorityMap = { high: 3, medium: 2, low: 1 };
				const priorityValue =
					priorityMap[recommendation.priority as keyof typeof priorityMap] || 2;

				// Sanitize and normalize content
				const title = sanitizeText(
					recommendation.title || recommendation.action || "",
				);
				const description = sanitizeText(
					recommendation.description || recommendation.reasoning || "",
				);
				const tf = String(recommendation.timeframe || "").toLowerCase();
				const timeframe = tf.includes("week")
					? "weekly"
					: tf.includes("day")
						? "daily"
						: tf.includes("month")
							? "monthly"
							: sanitizeText(recommendation.timeframe || "");

				// Save as corrective action
				const { error } = await supabase.from("corrective_actions").insert({
					reflection_id: reflectionId,
					title,
					description: timeframe
						? `${description}\n\nCadence: ${timeframe}`
						: description,
					priority: priorityValue,
					status: "not_started",
				});

				if (error) {
					console.error("Error saving corrective action:", error);
					alert(
						"Failed to convert recommendation to action. Please try again.",
					);
					return;
				}

				// Show success message
				alert("Recommendation successfully converted to action!");
			} catch (error) {
				console.error("Error converting recommendation:", error);
				alert("Failed to convert recommendation to action. Please try again.");
			} finally {
				setConvertingRecommendation(null);
			}
		};

		if (loading) {
			return (
				<Card
					className={`border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm ${className}`}
				>
					<CardContent className="flex items-center justify-center py-8">
						<RefreshCw className="h-6 w-6 animate-spin mr-2" />
						<span>Loading reflection analysis...</span>
					</CardContent>
				</Card>
			);
		}

		if (error) {
			return (
				<Card
					className={`border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm ${className}`}
				>
					<CardContent className="text-center py-8">
						<AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
						<h3 className="text-lg font-semibold mb-2">Analysis Error</h3>
						<p className="text-muted-foreground mb-4">{error}</p>
						{totalCheckInCount < 3 && (
							<div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
								<p className="text-sm text-yellow-800 dark:text-yellow-200">
									You need at least 3 check-ins in the last 28 days to generate
									an analysis. You currently have {totalCheckInCount} check-in
									{totalCheckInCount !== 1 ? "s" : ""} in the last 28 days.
								</p>
							</div>
						)}
						<Button
							onClick={generateAnalysis}
							disabled={generating || totalCheckInCount < 3}
						>
							{generating ? (
								<>
									<RefreshCw className="h-4 w-4 mr-2 animate-spin" />
									Generating...
								</>
							) : (
								"Generate Analysis"
							)}
						</Button>
					</CardContent>
				</Card>
			);
		}

		if (!analysis) {
			return (
				<Card
					className={`border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm ${className}`}
				>
					<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
								<Target className="h-5 w-5 text-white" />
							</div>
							<div>
								<CardTitle className="text-xl sm:text-2xl font-semibold">
									Reflection Analysis
								</CardTitle>
								<CardDescription>
									Get AI-powered insights into your progress with this
									reflection
								</CardDescription>
							</div>
						</div>
					</CardHeader>
					<CardContent className="text-center py-8">
						<Lightbulb className="h-12 w-12 mx-auto mb-4 text-primary" />
						<h3 className="text-lg font-semibold mb-2">
							No Analysis Available
						</h3>
						<p className="text-muted-foreground mb-4">
							Generate an analysis to see insights into your reflection progress
						</p>
						{totalCheckInCount < 3 && (
							<div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
								<p className="text-sm text-yellow-800 dark:text-yellow-200">
									You need at least 3 check-ins in the last 28 days to generate
									an analysis. You currently have {totalCheckInCount} check-in
									{totalCheckInCount !== 1 ? "s" : ""} in the last 28 days.
								</p>
							</div>
						)}
						<Button
							onClick={generateAnalysis}
							disabled={generating || totalCheckInCount < 3}
						>
							{generating ? (
								<>
									<RefreshCw className="h-4 w-4 mr-2 animate-spin" />
									Generating Analysis...
								</>
							) : (
								<>
									<RefreshCw className="h-4 w-4 mr-2" />
									Generate AI Analysis
								</>
							)}
						</Button>
					</CardContent>
				</Card>
			);
		}

		return (
			<div className={`space-y-6 ${className}`}>
				{/* Header */}
				<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
					<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
									<Target className="h-5 w-5 text-white" />
								</div>
								<div>
									<CardTitle className="text-xl sm:text-2xl font-semibold">
										{reflectionTitle} - Analysis
									</CardTitle>
									<CardDescription>
										AI-powered insights based on your check-in data
									</CardDescription>
								</div>
							</div>
							<div className="flex flex-col items-end gap-2">
								{totalCheckInCount < 3 && (
									<p className="text-xs text-yellow-600 dark:text-yellow-400">
										Requires 3 check-ins in last 28 days ({totalCheckInCount}{" "}
										available)
									</p>
								)}
								<Button
									variant="outline"
									size="sm"
									onClick={generateAnalysis}
									disabled={generating || totalCheckInCount < 3}
								>
									{generating ? (
										<>
											<RefreshCw className="h-4 w-4 mr-2 animate-spin" />
											Recalculating...
										</>
									) : (
										<>
											<RefreshCw className="h-4 w-4 mr-2" />
											Recalculate
										</>
									)}
								</Button>
							</div>
						</div>
					</CardHeader>
					<CardContent className="pt-6">
						<div className="flex items-center text-sm text-muted-foreground">
							<Clock className="h-4 w-4 mr-1" />
							Last updated {formatDistanceToNow(new Date(lastUpdated))} ago
							<span className="mx-2">•</span>
							Based on {checkInCount} check-ins
						</div>
					</CardContent>
				</Card>

				{/* Impact Trends removed per product decision */}

				{/* Positive and Negative Observations */}
				<div className="grid gap-6 md:grid-cols-2">
					{/* Positive Observations */}
					<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
						<CardHeader className="pb-4 border-b bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20">
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600">
									<CheckCircle className="h-5 w-5 text-white" />
								</div>
								<div>
									<CardTitle className="text-xl sm:text-2xl font-semibold">
										Positive Observations
									</CardTitle>
								</div>
							</div>
						</CardHeader>
						<CardContent className="pt-6">
							{analysis.positiveObservations.length > 0 ? (
								<div className="space-y-4">
									{analysis.positiveObservations.map((obs, index) => (
										<div
											key={index}
											className="border-l-4 border-green-500 pl-4 py-2 bg-green-50/50 dark:bg-green-900/10 rounded-r-lg print-remove-border"
										>
											<h4 className="font-semibold">
												{sanitizeText(obs.observation)}
											</h4>
											<p className="text-sm text-muted-foreground mt-1 leading-relaxed">
												{sanitizeText(obs.impact)}
											</p>
											<div className="flex items-center mt-2 text-xs text-muted-foreground">
												<span>
													Mentions: ~{approxMentions(obs.frequency)} of{" "}
													{Math.max(1, checkInCount)}
												</span>
												{obs.signals.length > 0 && (
													<>
														<span className="mx-2">•</span>
														<span className="print-hide-signals-count">
															Signals: {obs.signals.length}
														</span>
													</>
												)}
											</div>
										</div>
									))}
								</div>
							) : (
								<p className="text-muted-foreground text-sm">
									No positive observations identified yet.
								</p>
							)}
						</CardContent>
					</Card>

					{/* Negative Observations */}
					<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
						<CardHeader className="pb-4 border-b bg-gradient-to-r from-red-50/50 to-orange-50/50 dark:from-red-950/20 dark:to-orange-950/20">
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-lg bg-gradient-to-br from-red-500 to-orange-600">
									<AlertCircle className="h-5 w-5 text-white" />
								</div>
								<div>
									<CardTitle className="text-xl sm:text-2xl font-semibold">
										Areas for Attention
									</CardTitle>
								</div>
							</div>
						</CardHeader>
						<CardContent className="pt-6">
							{analysis.negativeObservations.length > 0 ? (
								<div className="space-y-4">
									{analysis.negativeObservations.map((obs, index) => (
										<div
											key={index}
											className="border-l-4 border-red-500 pl-4 py-2 bg-red-50/50 dark:bg-red-900/10 rounded-r-lg print-remove-border"
										>
											<h4 className="font-semibold">
												{sanitizeText(obs.observation)}
											</h4>
											<p className="text-sm text-muted-foreground mt-1 leading-relaxed">
												{sanitizeText(obs.impact)}
											</p>
											<div className="flex items-center mt-2 text-xs text-muted-foreground">
												<span>
													Mentions: ~{approxMentions(obs.frequency)} of{" "}
													{Math.max(1, checkInCount)}
												</span>
												{obs.signals.length > 0 && (
													<>
														<span className="mx-2 print-hide-signals-separator">
															•
														</span>
														<span className="print-hide-signals-count">
															Signals: {obs.signals.length}
														</span>
													</>
												)}
											</div>
										</div>
									))}
								</div>
							) : (
								<p className="text-muted-foreground text-sm">
									No concerning patterns identified.
								</p>
							)}
						</CardContent>
					</Card>
				</div>

				{/* Key Insights */}
				<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
					<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
								<Lightbulb className="h-5 w-5 text-white" />
							</div>
							<div>
								<CardTitle className="text-xl sm:text-2xl font-semibold">
									Check-in Insights
								</CardTitle>
							</div>
						</div>
					</CardHeader>
					<CardContent className="pt-6">
						<div className="grid gap-6 md:grid-cols-3">
							<div>
								<h4 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">
									Key Findings
								</h4>
								<ul className="space-y-2">
									{analysis.insights.keyFindings.map((finding, index) => (
										<li
											key={index}
											className="text-sm text-muted-foreground flex items-start leading-relaxed"
										>
											<span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0" />
											{sanitizeText(finding)}
										</li>
									))}
								</ul>
							</div>
							<div>
								<h4 className="font-semibold mb-2 text-green-700 dark:text-green-400">
									Progress Indicators
								</h4>
								<ul className="space-y-2">
									{analysis.insights.progressIndicators.map(
										(indicator, index) => (
											<li
												key={index}
												className="text-sm text-muted-foreground flex items-start leading-relaxed"
											>
												<span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0" />
												{sanitizeText(indicator)}
											</li>
										),
									)}
								</ul>
							</div>
							<div>
								<h4 className="font-semibold mb-2 text-orange-700 dark:text-orange-400">
									Concern Areas
								</h4>
								<ul className="space-y-2">
									{analysis.insights.concernAreas.map((concern, index) => (
										<li
											key={index}
											className="text-sm text-muted-foreground flex items-start leading-relaxed"
										>
											<span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0" />
											{sanitizeText(concern)}
										</li>
									))}
								</ul>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Recommended Actions */}
				<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
					<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
								<Target className="h-5 w-5 text-white" />
							</div>
							<div>
								<CardTitle className="text-xl sm:text-2xl font-semibold">
									Recommended Actions
								</CardTitle>
							</div>
						</div>
					</CardHeader>
					<CardContent className="pt-6">
						<div className="space-y-4">
							{analysis.recommendations.length === 0 ? (
								<p className="text-muted-foreground text-center py-4">
									{analysis.recommendations.length === 0
										? "No recommendations available."
										: "All recommendations have been converted to actions or ignored."}
								</p>
							) : (
								analysis.recommendations
									.map((rec, index) => {
										const isEditing = editingRecIndex === index;

										return (
											<div
												key={index}
												className="border-2 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 shadow-md print-remove-border"
											>
												{isEditing ? (
													<div className="space-y-3">
														<div>
															<label className="text-xs font-medium block mb-1">
																Title
															</label>
															<Input
																value={draftTitle}
																onChange={(e) => setDraftTitle(e.target.value)}
															/>
														</div>
														<div>
															<label className="text-xs font-medium block mb-1">
																Description
															</label>
															<Textarea
																value={draftDescription}
																onChange={(e) =>
																	setDraftDescription(e.target.value)
																}
																rows={3}
															/>
														</div>
														<div className="flex gap-2 justify-end">
															<Button
																size="sm"
																variant="secondary"
																onClick={() => {
																	setEditingRecIndex(null);
																	setDraftTitle("");
																	setDraftDescription("");
																}}
															>
																Cancel
															</Button>
															<Button
																size="sm"
																onClick={() => {
																	const updated = [...analysis.recommendations];
																	updated[index] = {
																		...rec,
																		title: draftTitle || rec.title,
																		description:
																			draftDescription || rec.description,
																	} as any;
																	setAnalysis({
																		...(analysis as any),
																		recommendations: updated,
																	} as any);
																	setEditingRecIndex(null);
																	setDraftTitle("");
																	setDraftDescription("");
																}}
															>
																Save
															</Button>
														</div>
													</div>
												) : (
													<>
														<div className="flex items-start justify-between mb-2">
															<h4 className="font-semibold">
																{sanitizeText(
																	rec.title ||
																		rec.action ||
																		rec.recommendation ||
																		rec.suggestion ||
																		"Untitled Recommendation",
																)}
															</h4>
															{rec.priority && (
																<div className="flex items-center space-x-2">
																	<Badge
																		variant={
																			getPriorityColor(rec.priority) as any
																		}
																	>
																		{rec.priority.charAt(0).toUpperCase() +
																			rec.priority.slice(1)}
																	</Badge>
																</div>
															)}
														</div>
														<p className="text-sm text-muted-foreground mb-2 leading-relaxed">
															{sanitizeText(
																rec.description ||
																	rec.reasoning ||
																	rec.reason ||
																	"No description provided.",
															)}
														</p>
														<div className="flex items-center justify-between">
															{rec.timeframe && (
																<div className="text-xs text-muted-foreground">
																	<Clock className="h-3 w-3 inline mr-1" />
																	{rec.timeframe}
																</div>
															)}
															<div className="flex items-center gap-2">
																<Button
																	size="sm"
																	variant="outline"
																	onClick={() => {
																		setEditingRecIndex(index);
																		setDraftTitle(
																			rec.title ||
																				rec.action ||
																				rec.recommendation ||
																				rec.suggestion ||
																				"",
																		);
																		setDraftDescription(
																			rec.description ||
																				rec.reasoning ||
																				rec.reason ||
																				"",
																		);
																	}}
																>
																	Edit
																</Button>
																<Button
																	size="sm"
																	variant="outline"
																	onClick={() =>
																		convertRecommendationToAction(rec, index)
																	}
																	disabled={convertingRecommendation === index}
																>
																	{convertingRecommendation === index ? (
																		<>
																			<RefreshCw className="h-3 w-3 mr-1 animate-spin" />
																			Converting...
																		</>
																	) : (
																		<>
																			<Plus className="h-3 w-3 mr-1" />
																			Convert to Action
																		</>
																	)}
																</Button>
															</div>
														</div>
													</>
												)}
											</div>
										);
									})
									.filter(Boolean)
							)}
						</div>
					</CardContent>
				</Card>

				{/* Signal Patterns */}
				{analysis.signalPatterns.length > 0 && (
					<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
						<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
									<Activity className="h-5 w-5 text-white" />
								</div>
								<div>
									<CardTitle className="text-xl sm:text-2xl font-semibold">
										Signal Patterns
									</CardTitle>
								</div>
							</div>
						</CardHeader>
						<CardContent className="pt-6">
							<div className="grid gap-4 md:grid-cols-2">
								{analysis.signalPatterns.map((pattern, index) => (
									<div
										key={index}
										className="border-2 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 shadow-md print-remove-border"
									>
										<h4 className="font-semibold">
											{formatSignal(pattern.signal)}
										</h4>
										<p className="text-sm text-muted-foreground mt-1 leading-relaxed">
											{sanitizeText(pattern.correlation)}
										</p>
										<div className="flex items-center mt-2 text-xs text-muted-foreground">
											<span>
												Mentions: ~{approxMentions(pattern.frequency)} of{" "}
												{Math.max(1, checkInCount)}
											</span>
											<span className="mx-2">•</span>
											<span>{pattern.impact}</span>
										</div>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				)}
			</div>
		);
	},
);

ReflectionAnalysis.displayName = "ReflectionAnalysis";
